/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/**
 * Nordic By Hand — required terms acceptance before checkout.
 *
 * The choice is persisted as cart attributes. `terms_version` deliberately
 * forces a fresh acceptance whenever the terms version changes.
 */
(() => {
  if (window.NBHTermsConsentInitialized) return;
  window.NBHTermsConsentInitialized = true;

  const TERMS_URL = '/pages/handelsbetingelser';
  const TERMS_VERSION = '2026-09-18';
  const CHECKOUT_SELECTOR = '#checkout, #CartDrawer-Checkout';
  const FAST_CHECKOUT_SELECTOR = '.additional-checkout-buttons, .cart__dynamic-checkout-buttons';

  const state = {
    accepted: false,
    loaded: false,
    saving: false,
  };

  function injectStyles() {
    if (document.getElementById('nbh-terms-consent-styles')) return;
    const style = document.createElement('style');
    style.id = 'nbh-terms-consent-styles';
    style.textContent = `
      .nbh-terms-consent {
        margin: 1.6rem 0 1.2rem;
        padding-top: 1.4rem;
        border-top: 1px solid rgba(var(--color-foreground), 0.16);
      }
      .nbh-terms-consent__label {
        display: grid;
        grid-template-columns: 1.9rem minmax(0, 1fr);
        gap: 0.9rem;
        align-items: start;
        cursor: pointer;
        font-size: 1.3rem;
        line-height: 1.45;
      }
      .nbh-terms-consent__checkbox {
        width: 1.8rem;
        height: 1.8rem;
        margin: 0.1rem 0 0;
        cursor: pointer;
        accent-color: rgb(var(--color-foreground));
      }
      .nbh-terms-consent__checkbox:disabled {
        cursor: wait;
      }
      .nbh-terms-consent a {
        color: inherit;
        text-decoration: underline;
        text-underline-offset: 0.2rem;
      }
      .nbh-terms-consent__hint,
      .nbh-terms-consent__status {
        margin: 0.55rem 0 0 2.8rem;
        font-size: 1.15rem;
        line-height: 1.4;
        opacity: 0.72;
      }
      .nbh-terms-consent__status:empty {
        display: none;
      }
      .cart__checkout-button:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }
    `;
    document.head.appendChild(style);
  }

  function checkoutButtons() {
    return [...document.querySelectorAll(CHECKOUT_SELECTOR)];
  }

  function getCtaParent(button) {
    const ctas = button.closest('.cart__ctas');
    return ctas?.parentElement || button.parentElement;
  }

  function consentKey(button) {
    return button.id === 'CartDrawer-Checkout' ? 'drawer' : 'cart';
  }

  function ensureConsent(button) {
    const parent = getCtaParent(button);
    const ctas = button.closest('.cart__ctas') || button.parentElement;
    if (!parent || !ctas) return null;

    const key = consentKey(button);
    let consent = parent.querySelector(`[data-nbh-terms-consent="${key}"]`);
    if (consent) return consent;

    const checkboxId = `nbh-terms-consent-${key}`;
    consent = document.createElement('div');
    consent.className = 'nbh-terms-consent';
    consent.dataset.nbhTermsConsent = key;
    consent.innerHTML = `
      <label class="nbh-terms-consent__label" for="${checkboxId}">
        <input
          class="nbh-terms-consent__checkbox"
          id="${checkboxId}"
          type="checkbox"
          data-nbh-terms-checkbox
        >
        <span>Jeg har læst og accepterer <a href="${TERMS_URL}" target="_blank" rel="noopener">handelsbetingelserne</a>.</span>
      </label>
      <p class="nbh-terms-consent__hint">Påkrævet før du kan gå til betaling.</p>
      <p class="nbh-terms-consent__status" data-nbh-terms-status aria-live="polite"></p>
    `;
    parent.insertBefore(consent, ctas);
    return consent;
  }

  function cartLooksEmpty(button) {
    return Boolean(button.closest('.is-empty'));
  }

  function setStatus(message = '') {
    document.querySelectorAll('[data-nbh-terms-status]').forEach((node) => {
      node.textContent = message;
    });
  }

  function syncFastCheckout(parent, ready) {
    if (!parent) return;
    parent.querySelectorAll(FAST_CHECKOUT_SELECTOR).forEach((node) => {
      if (node.dataset.nbhTermsOriginalHidden === undefined) {
        node.dataset.nbhTermsOriginalHidden = node.hidden ? 'true' : 'false';
      }
      const originallyHidden = node.dataset.nbhTermsOriginalHidden === 'true';
      node.hidden = originallyHidden || !ready;
    });
  }

  function syncUi() {
    injectStyles();
    const ready = state.loaded && state.accepted && !state.saving;

    checkoutButtons().forEach((button) => {
      const consent = ensureConsent(button);
      const checkbox = consent?.querySelector('[data-nbh-terms-checkbox]');
      if (checkbox) {
        checkbox.checked = state.accepted;
        checkbox.disabled = !state.loaded || state.saving;
      }

      button.disabled = cartLooksEmpty(button) || !ready;
      button.setAttribute('aria-disabled', String(button.disabled));
      syncFastCheckout(getCtaParent(button), ready);
    });
  }

  async function fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
      const cart = await response.json();
      const attributes = cart.attributes || {};
      state.accepted =
        attributes.terms_accepted === 'true' &&
        attributes.terms_version === TERMS_VERSION;
    } catch (error) {
      console.error('[Nordic By Hand] Could not read terms consent state.', error);
      state.accepted = false;
    } finally {
      state.loaded = true;
      syncUi();
    }
  }

  async function saveConsent(accepted) {
    if (state.saving) return;

    const previous = state.accepted;
    state.accepted = accepted;
    state.saving = true;
    setStatus('Gemmer dit valg…');
    syncUi();

    const acceptedAt = accepted ? new Date().toISOString() : '';

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            terms_accepted: accepted ? 'true' : '',
            terms_version: accepted ? TERMS_VERSION : '',
            terms_accepted_at: acceptedAt,
          },
        }),
      });

      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
      state.accepted = accepted;
      setStatus(accepted ? 'Handelsbetingelserne er accepteret.' : '');
    } catch (error) {
      console.error('[Nordic By Hand] Could not save terms consent.', error);
      state.accepted = previous;
      setStatus('Dit valg kunne ikke gemmes. Prøv igen.');
    } finally {
      state.saving = false;
      syncUi();
    }
  }

  document.addEventListener('change', (event) => {
    const checkbox = event.target.closest?.('[data-nbh-terms-checkbox]');
    if (!checkbox) return;
    saveConsent(checkbox.checked);
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target.closest?.('#checkout, #CartDrawer-Checkout, a[href*="/checkout"]');
      if (!target) return;

      const isCartCheckout =
        target.matches('#checkout, #CartDrawer-Checkout') ||
        Boolean(target.closest('cart-drawer, .cart__footer, [id*="main-cart-footer" i]'));
      if (!isCartCheckout || (state.loaded && state.accepted && !state.saving)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      setStatus('Du skal acceptere handelsbetingelserne, før du kan gå til betaling.');
      const parent = target.matches('#checkout, #CartDrawer-Checkout') ? getCtaParent(target) : target.parentElement;
      parent?.querySelector('[data-nbh-terms-checkbox]')?.focus();
    },
    true,
  );

  let mutationScheduled = false;
  const observer = new MutationObserver(() => {
    if (mutationScheduled) return;
    mutationScheduled = true;
    requestAnimationFrame(() => {
      mutationScheduled = false;
      syncUi();
    });
  });

  function initTermsConsent() {
    syncUi();
    observer.observe(document.body, { childList: true, subtree: true });
    fetchCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTermsConsent, { once: true });
  } else {
    initTermsConsent();
  }
})();
